import { Currency, CurrencyAmount } from '@etcswapv2/sdk-legacy'
import { useMemo } from 'react'
import { usePublicClient, useWalletClient } from 'wagmi'
import { type Address, encodeFunctionData } from 'viem'
import { ROUTER_ADDRESS } from '../constants'
import { routerAbi } from '../constants/abis'
import { useTransactionAdder } from '../state/transactions/hooks'
import { calculateGasMargin, calculateSlippageAmount } from '../utils'
import { wrappedCurrency } from '../utils/wrappedCurrency'
import { useActiveWeb3React } from './index'
import { getChainById } from '../wagmi'
import { Field } from '../state/mint/actions'

export enum AddLiquidityCallbackState {
  INVALID,
  LOADING,
  VALID
}

interface AddLiquidityCallbackParams {
  currencyA: Currency | undefined
  currencyB: Currency | undefined
  parsedAmounts: { [field in Field]?: CurrencyAmount }
  noLiquidity: boolean
  allowedSlippage: number
  deadline: number
}

export function useAddLiquidityCallback({
  currencyA,
  currencyB,
  parsedAmounts,
  noLiquidity,
  allowedSlippage,
  deadline
}: AddLiquidityCallbackParams): {
  state: AddLiquidityCallbackState
  callback: null | (() => Promise<string>)
  error: string | null
} {
  const { account, chainId } = useActiveWeb3React()
  const publicClient = usePublicClient()
  const { data: walletClient } = useWalletClient()

  const addTransaction = useTransactionAdder()

  return useMemo(() => {
    if (!publicClient || !walletClient || !account || !chainId) {
      return { state: AddLiquidityCallbackState.INVALID, callback: null, error: 'Missing dependencies' }
    }

    const parsedAmountA = parsedAmounts[Field.CURRENCY_A]
    const parsedAmountB = parsedAmounts[Field.CURRENCY_B]

    if (!parsedAmountA || !parsedAmountB || !currencyA || !currencyB) {
      return { state: AddLiquidityCallbackState.INVALID, callback: null, error: 'Enter amounts' }
    }

    const routerAddress = ROUTER_ADDRESS[chainId] as Address | undefined
    if (!routerAddress) {
      return { state: AddLiquidityCallbackState.INVALID, callback: null, error: 'Router not found for this chain' }
    }

    const chain = getChainById(chainId)
    if (!chain) {
      return { state: AddLiquidityCallbackState.INVALID, callback: null, error: 'Unsupported chain' }
    }

    return {
      state: AddLiquidityCallbackState.VALID,
      callback: async function onAdd(): Promise<string> {
        const amountsMin = {
          [Field.CURRENCY_A]: calculateSlippageAmount(parsedAmountA, noLiquidity ? 0 : allowedSlippage)[0],
          [Field.CURRENCY_B]: calculateSlippageAmount(parsedAmountB, noLiquidity ? 0 : allowedSlippage)[0]
        }

        const deadlineFromNow = BigInt(Math.ceil(Date.now() / 1000) + deadline)

        let data: `0x${string}`
        let value: bigint = 0n

        // Use property check instead of reference equality to avoid module duplication issues
        const currencyAIsNative = currencyA && !('address' in currencyA)
        const currencyBIsNative = currencyB && !('address' in currencyB)

        if (currencyAIsNative || currencyBIsNative) {
          // addLiquidityETC
          const tokenBIsETH = currencyBIsNative
          const token = wrappedCurrency(tokenBIsETH ? currencyA : currencyB, chainId)

          if (!token) {
            throw new Error('Could not wrap currency')
          }

          data = encodeFunctionData({
            abi: routerAbi,
            functionName: 'addLiquidityETC',
            args: [
              token.address as Address, // token
              BigInt((tokenBIsETH ? parsedAmountA : parsedAmountB).raw.toString()), // amountTokenDesired
              BigInt(amountsMin[tokenBIsETH ? Field.CURRENCY_A : Field.CURRENCY_B].toString()), // amountTokenMin
              BigInt(amountsMin[tokenBIsETH ? Field.CURRENCY_B : Field.CURRENCY_A].toString()), // amountETCMin
              account as Address, // to
              deadlineFromNow // deadline
            ]
          })
          value = BigInt((tokenBIsETH ? parsedAmountB : parsedAmountA).raw.toString())
        } else {
          // addLiquidity (token/token)
          const tokenA = wrappedCurrency(currencyA, chainId)
          const tokenB = wrappedCurrency(currencyB, chainId)

          if (!tokenA || !tokenB) {
            throw new Error('Could not wrap currencies')
          }

          data = encodeFunctionData({
            abi: routerAbi,
            functionName: 'addLiquidity',
            args: [
              tokenA.address as Address, // tokenA
              tokenB.address as Address, // tokenB
              BigInt(parsedAmountA.raw.toString()), // amountADesired
              BigInt(parsedAmountB.raw.toString()), // amountBDesired
              BigInt(amountsMin[Field.CURRENCY_A].toString()), // amountAMin
              BigInt(amountsMin[Field.CURRENCY_B].toString()), // amountBMin
              account as Address, // to
              deadlineFromNow // deadline
            ]
          })
        }

        try {
          // Estimate gas
          const gasEstimate = await publicClient.estimateGas({
            to: routerAddress,
            data,
            value,
            account: account as Address
          })

          // Add 20% gas margin
          const gasLimit = calculateGasMargin(gasEstimate)

          // Send transaction
          const hash = await walletClient.sendTransaction({
            to: routerAddress,
            data,
            value,
            gas: gasLimit,
            chain,
            account: account as Address
          } as unknown as Parameters<typeof walletClient.sendTransaction>[0])

          const summary =
            'Add ' +
            parsedAmountA.toSignificant(3) +
            ' ' +
            currencyA.symbol +
            ' and ' +
            parsedAmountB.toSignificant(3) +
            ' ' +
            currencyB.symbol

          addTransaction({ hash }, { summary })

          return hash
        } catch (error) {
          // if the user rejected the tx, pass this along
          if (
            (error as { code?: number })?.code === 4001 ||
            (error as Error)?.message?.includes('rejected')
          ) {
            throw new Error('Transaction rejected.')
          } else {
            console.error(`Add liquidity failed`, error)
            throw new Error(`Add liquidity failed: ${(error as Error).message}`)
          }
        }
      },
      error: null
    }
  }, [
    publicClient,
    walletClient,
    account,
    chainId,
    currencyA,
    currencyB,
    parsedAmounts,
    noLiquidity,
    allowedSlippage,
    deadline,
    addTransaction
  ])
}
